// ===========================================
// Consent Gate (#442)
// ===========================================
//
// Rendered by ProtectedRoute INSTEAD of the app while the signed-in user still has to agree to
// the school's T&Cs (`user.requiresConsentAcknowledgement`, reported by login, 2FA and
// /auth/me). The server refuses everything else meanwhile (403 CONSENT_REQUIRED), so this is
// what a parent sees after a login, a 2FA login, a page refresh or a click in an open tab.
// Rendering it instead of AdminLayout means none of the layout's queries (or its 30 s unread
// poll) ever fire while it shows.

import { Box, Button, Paper, Typography, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/useAuth';
import { useTenant } from '../../contexts/TenantContext';
import { useLegalSettings } from '../../hooks/useAdminSettings';
import ConsentModal from './ConsentModal';

/** Read once by SchoolLoginPage after a decline logs the parent out (survives a hard redirect). */
export const LOGIN_NOTICE_KEY = 'mnm.loginNotice';

function setLoginNotice(message: string) {
  try {
    sessionStorage.setItem(LOGIN_NOTICE_KEY, message);
  } catch {
    // Private mode / blocked storage: the parent is still logged out, just without the notice.
  }
}

export default function ConsentGate() {
  const { user, refreshUser, logout } = useAuth();
  const { school } = useTenant();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const legal = useLegalSettings();

  const schoolName = school?.name || user?.schoolName || '';
  const schoolLabel = schoolName || 'your school';
  const logoUrl = school?.branding?.logoUrl || '/music-n-me-logo.png';
  const kind = user?.consentKind ?? 'PRIMARY';

  // D4: the family's primary contact declined. A second contact has nothing to accept; the app
  // is paused for the whole family until the primary accepts again.
  if (kind === 'AWAITING_PRIMARY') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#FCF6E6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Paper
          variant="outlined"
          sx={{ maxWidth: 520, width: '100%', p: { xs: 3, sm: 4 }, textAlign: 'center', borderRadius: 2 }}
        >
          <Box
            component="img"
            src={logoUrl}
            alt={schoolName}
            sx={{ display: 'block', mx: 'auto', mb: 2, maxHeight: { xs: 56, sm: 72 }, maxWidth: 160 }}
          />
          <Typography
            variant="h6"
            component="h1"
            fontWeight={700}
            sx={{ fontFamily: theme.typography.h4.fontFamily, mb: 1.5 }}
          >
            Your family&apos;s account is paused
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Your family&apos;s primary contact has declined the Terms &amp; Conditions, so the app is
            paused for your family. Please ask them to log in and accept, or contact {schoolLabel}.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            {/* Once the primary accepts, this re-reads the session and the app opens (#442 /qa L10) */}
            <Button variant="contained" onClick={() => void refreshUser()}>
              Check again
            </Button>
            <Button variant="outlined" onClick={() => void logout()}>
              Log out
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  const handleConsented = async () => {
    // /auth/me now reports no requirement, so ProtectedRoute renders the page the parent asked
    // for (a deep link stays where it was).
    await refreshUser();
    await queryClient.invalidateQueries();
  };

  const handleDeclined = async (declined: boolean) => {
    // The decline already ended the session on the server. Leave the notice where the login
    // page will find it, then log out: ProtectedRoute (or the API client's own redirect)
    // takes the parent to the login page (plan /qa A10). Only claim the school was told when
    // the decline actually went through (#442 /qa L5).
    setLoginNotice(
      declined
        ? 'You have been logged out. Your school has been notified.'
        : 'You have been logged out.'
    );
    await logout();
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FCF6E6' }}>
      <ConsentModal
        open
        kind={kind}
        familyId={user?.familyId ?? ''}
        contentLoading={legal.isLoading}
        termsContent={legal.data?.termsOfService ?? null}
        privacyContent={legal.data?.privacyPolicy ?? null}
        schoolName={schoolName}
        logoUrl={logoUrl}
        onConsented={handleConsented}
        onDeclined={handleDeclined}
      />
    </Box>
  );
}
